import { useCallback, useState } from 'react';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { getFormattedDate } from 'lib/common';
import { getEmailLogTypes } from 'lib/email';
import {
  EmptyIndicator,
  Pagination,
  TimeIndicator,
  Tooltip
} from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';

interface Props {
  isUserProfile?: boolean;
  userId?: number;
}

const EmailLog = ({ isUserProfile = false, userId }: Props) => {
  const { language, messages } = useReduxState();

  const [page, setPage] = useState(1);

  const { data, error, loading, updateData } = useApiData({
    url: `/admin/email-log-entries?page=${page}${
      userId ? `&userId=${userId}` : ''
    }`
  });

  const updatePage = useCallback(
    ({ page }) => setPage(page),
    [setPage, updateData]
  );

  const emailLogTypes = getEmailLogTypes({ messages });

  return (
    <div className="email-log">
      <Box loading={loading} padding={0} title={messages.emailLog}>
        {error || !data?.emailLogEntries?.length ? (
          <EmptyIndicator icon="envelope" title={messages.emailLogEmpty} />
        ) : (
          <BoxTable>
            <BoxTableHeader>
              <BoxTableCell>{messages.commonType}</BoxTableCell>
              {!isUserProfile ? (
                <BoxTableCell>{messages.commonRecipient}</BoxTableCell>
              ) : null}
              <BoxTableCell>{messages.commonSubject}</BoxTableCell>
              <BoxTableCell>{messages.commonTime}</BoxTableCell>
            </BoxTableHeader>

            {data.emailLogEntries.map((entry) => (
              <BoxTableRow key={entry.id}>
                <BoxTableCell>{emailLogTypes[entry.type]}</BoxTableCell>
                {!isUserProfile ? (
                  <BoxTableCell>{entry.emailAddress}</BoxTableCell>
                ) : null}
                <BoxTableCell>{entry.subject}</BoxTableCell>
                <BoxTableCell>
                  <Tooltip
                    content={getFormattedDate({
                      date: entry.createdAt,
                      dateFormat: 'dd.MM.y HH:mm'
                    })}
                  >
                    <TimeIndicator
                      language={language}
                      timeString={entry.createdAt}
                    />
                  </Tooltip>
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        )}
      </Box>

      <Pagination
        currentPage={page}
        numberOfPages={data?.pages}
        onPageChange={updatePage}
      />
    </div>
  );
};

export default EmailLog;
